import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getLabels,
  createLabel,
  updateLabel,
  deleteLabel,
} from '../../../../../services/api';
import { LABEL_TYPES, MANUAL_COLOR } from '../utils/labelConstants';
import { mapApiLabel, mapDraftToApiPayload } from '../utils/labelApiMapper';
import { setLabelColor } from '../utils/labelColorStore';

const useLabelCrud = (graphId, sensorIds, graphCurves, dateOptions) => {
  const [localLabels, setLocalLabels] = useState([]);
  const [loadingAnnotations, setLoading] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [creatingLabel, setCreatingLabel] = useState(null);
  const [labelError, setLabelError] = useState(null);
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const timerEtiquette = useRef(null);

  useEffect(
    () => () => {
      if (timerEtiquette.current) clearTimeout(timerEtiquette.current);
    },
    []
  );

  // ── Chargement API ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!graphId || !sensorIds?.length) return;
    setLocalLabels([]);
    setLoading(true);
    const promises = sensorIds.map((sensorId) =>
      getLabels({ sensorId, graphId })
        .then((res) => {
          const labels = res?.data?.data;
          if (Array.isArray(labels))
            setLocalLabels((prev) => [...prev, ...labels.map(mapApiLabel)]);
        })
        .catch((err) => console.error('[useLabelCrud] getLabels error:', err))
    );
    Promise.all(promises).finally(() => setLoading(false));
  }, [graphId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Créer un label depuis la barre d'outils ───────────────────────────────────
  const addLabel = useCallback(
    (type = 'JALON') => {
      if (!dateOptions.length) return;
      const labelConfig = LABEL_TYPES[type];
      const midPoint = dateOptions[Math.floor(dateOptions.length / 2)];
      setCreatingLabel({
        id: null,
        t: midPoint.ts,
        pk: midPoint.pk,
        iconType: labelConfig.iconType,
        labelType: type,
        label: '',
        note: '',
        color: MANUAL_COLOR,
        sensorAndPosition: graphCurves[0]?.formula || '',
      });
    },
    [dateOptions, graphCurves]
  );

  // ── Créer un label depuis le survol du signal ─────────────────────────────────
  const handleAddLabelFromSignal = useCallback(
    ({ ts, yVal, curveIndex, formula, pointNumber }) => {
      if (!dateOptions.length) return;
      const closest = dateOptions.reduce((prev, curr) =>
        Math.abs(curr.ts - ts) < Math.abs(prev.ts - ts) ? curr : prev
      );
      const selectedFormula =
        formula ||
        graphCurves[curveIndex ?? 0]?.formula ||
        graphCurves[0]?.formula ||
        '';
      setCreatingLabel({
        id: null,
        t: ts,
        pointIndex: pointNumber ?? null,
        pk: closest.pk,
        iconType: LABEL_TYPES.JALON.iconType,
        labelType: 'JALON',
        label: '',
        note:
          yVal !== undefined ? `Valeur : ${Math.round(yVal * 10) / 10}` : '',
        color: MANUAL_COLOR,
        sensorAndPosition: selectedFormula,
      });
    },
    [dateOptions, graphCurves]
  );

  // ── Sauvegarder (création ou modification) ────────────────────────────────────
  const handleSaveLabel = useCallback(async (draft) => {
    try {
      const payload = mapDraftToApiPayload(draft);
      const fallback = (raw, id) => ({
        id: raw?.id ?? id,
        time_unix: draft.t,
        pk_test_ref: draft.pk,
        label_text: draft.label ?? '',
        source: draft.labelType === 'IA' ? 'IA' : 'MANUAL',
        id_sensor: payload.id_sensor,
        position_in_sensor: payload.position_in_sensor,
      });
      let savedLabel;
      if (draft.id == null) {
        const res = await createLabel(payload);
        const raw = res.data?.data ?? res.data;
        savedLabel =
          raw?.time_unix != null || raw?.t != null
            ? raw
            : fallback(raw, draft.id);
      } else {
        const res = await updateLabel({ labelId: draft.id, ...payload });
        const raw = res.data?.data ?? res.data;
        savedLabel =
          raw?.time_unix != null || raw?.t != null
            ? raw
            : fallback(raw, draft.id);
      }
      // Couleur partagée par nom (Jalons). On la persiste AVANT le mapping : mapApiLabel
      // la relit du store local, puis on la propage à tous les labels de même nom.
      const isManual = draft.labelType !== 'IA';
      const name = (draft.label ?? '').trim();
      if (isManual) setLabelColor(name, draft.color);

      setLocalLabels((prev) => {
        const mapped = mapApiLabel(savedLabel);
        const next =
          draft.id == null
            ? [...prev, mapped]
            : prev.map((l) => (l.id === savedLabel.id ? mapped : l));
        if (!isManual) return next;
        return next.map((l) =>
          l.labelType !== 'IA' && (l.label ?? '').trim() === name
            ? { ...l, color: draft.color }
            : l
        );
      });
      setEditingLabel(null);
      setCreatingLabel(null);
    } catch (err) {
      const detail = err?.response?.data?.detail ?? '';
      if (
        detail.includes('uniq_sensor_position_time') ||
        detail.includes('Duplicate entry')
      ) {
        setLabelError(
          'Un label existe déjà pour ce capteur à cette date et position.'
        );
      } else {
        setLabelError('Erreur lors de la sauvegarde. Veuillez réessayer.');
      }
    }
  }, []);

  // ── Supprimer ─────────────────────────────────────────────────────────────────
  const handleRemoveLabel = useCallback(async () => {
    if (!hoveredLabel) return;
    const { id } = hoveredLabel.label;
    try {
      await deleteLabel({ labelId: id });
      setLocalLabels((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      console.error('[useLabelCrud] deleteLabel error:', err);
    }
    setHoveredLabel(null);
  }, [hoveredLabel]);

  // ── Ouvrir l'édition depuis la carte de survol ────────────────────────────────
  const handlePencilEdit = useCallback(() => {
    if (hoveredLabel) setEditingLabel({ ...hoveredLabel.label });
  }, [hoveredLabel]);

  return {
    localLabels,
    loadingAnnotations,
    editingLabel,
    setEditingLabel,
    creatingLabel,
    setCreatingLabel,
    labelError,
    setLabelError,
    hoveredLabel,
    setHoveredLabel,
    timerEtiquette,
    addLabel,
    handleAddLabelFromSignal,
    handleSaveLabel,
    handleRemoveLabel,
    handlePencilEdit,
  };
};

export default useLabelCrud;
